'use client';

import { MarketType } from '@drift-labs/sdk';
import {
	ENUM_UTILS,
	MarketId,
	UISerializableOrderActionRecord,
	sortUIOrderActionRecords,
} from '@drift/common';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { dlog } from 'src/dev';
import { OrderMatcherContext } from 'src/providers/orderRecordMatcherProvider';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useLazySubAccounts from './useLazySubAccounts';
import useAppEventEmitter from './useAppEventEmitter';
import { useMarketRecentTradesCache } from './useMarketRecentTradesCache';
import { useLoadingQueue } from './driftActions/transactions/useLoadingQueue';
import OrderRecordAndActionStateHandler from '../utils/OrderRecordAndActionMatcher';
import { MAX_RECENT_TRADES_TO_CACHE } from '../constants/constants';

/**
 * This hook processes new market trades and adds them to the recent trade history of the current market.
 * It also handles fetching the initial market history from the history server when the market changes.
 */
const useMarketHistory = () => {
	const setDriftState = useDriftStore((s) => s.set);
	const userAccounts = useLazySubAccounts();
	const currentMarket = useDriftStore((s) => s.selectedMarket.current);
	const { addLoadingItemToQueue, removeLoadingItemFromQueue } =
		useLoadingQueue();
	const orderMatcher = useContext(OrderMatcherContext);
	const appEventEmitter = useAppEventEmitter();

	const handleTradesForUserHistory = useCallback(
		(trades: UISerializableOrderActionRecord[]) => {
			// Find trades which belong to the current user (as taker or maker) and add to order matcher
			trades.forEach((trade) => {
				const matchingTaker = trade.taker
					? userAccounts.find((account) => account.pubKey.equals(trade.taker!))
							?.userKey
					: undefined;

				const matchingMaker = trade.maker
					? userAccounts.find((account) => account.pubKey.equals(trade.maker!))
							?.userKey
					: undefined;

				if (matchingTaker || matchingMaker) {
					orderMatcher?.addActionRecord(trade);
				}
			});
		},
		[userAccounts, orderMatcher]
	);

	const handleTradesForUserHistoryRef = useRef(handleTradesForUserHistory);
	handleTradesForUserHistoryRef.current = handleTradesForUserHistory;

	const { data: cachedMarketTrades, isLoading: isLoadingCachedMarketTrades } =
		useMarketRecentTradesCache(currentMarket?.symbol);

	const handleNewTradesResult = useCallback(
		({
			trades,
			mostRecentTradeScore,
		}: {
			trades: UISerializableOrderActionRecord[];
			mostRecentTradeScore: number;
		}) => {
			if (!trades || trades.length === 0) {
				dlog(`useMarketHistory`, `SkippingHandleNewTradesResult`, trades);
				return;
			}

			setDriftState((s) => {
				const _currentMarket = s.selectedMarket.current;
				const _currentMarketId = _currentMarket?.marketId;
				const tradesArePerp = ENUM_UTILS.match(
					trades[0].marketType,
					MarketType.PERP
				);
				const tradesMarketId = tradesArePerp
					? MarketId.createPerpMarket(trades[0].marketIndex)
					: MarketId.createSpotMarket(trades[0].marketIndex);

				// # Handle if any of the trades affect the current selected market history
				// Skip adding records to market history if they don't match the currently selected market at the time of adding it to the store
				if (!_currentMarketId?.equals(tradesMarketId)) {
					dlog(`useMarketHistory`, `SkippingUpdatingMarketTradeHistory`);
					return;
				}

				const currentTrades = s.marketTradeHistory?.trades ?? [];
				const tradesToClean = [...trades, ...currentTrades];
				const newTradesState =
					OrderRecordAndActionStateHandler.getSortedDedupedTrimmedRecords(
						tradesToClean.filter((trade) =>
							_currentMarketId.equals(
								new MarketId(trade.marketIndex, trade.marketType)
							)
						),
						MAX_RECENT_TRADES_TO_CACHE
					);

				s.marketTradeHistory = {
					market: _currentMarket ?? null,
					trades: newTradesState,
					mostRecentTradeScore,
				};
			});

			// # Handle if any of the trades affect the user's order history
			handleTradesForUserHistoryRef.current(trades);

			if (currentMarket) {
				appEventEmitter.emit('recentTradesLoaded', currentMarket.key);
			}
			removeLoadingItemFromQueue({ key: 'recentTrades' });
		},
		[setDriftState, removeLoadingItemFromQueue, appEventEmitter]
	);

	// reset state on market change

	useEffect(() => {
		if (isLoadingCachedMarketTrades) {
			addLoadingItemToQueue({ key: 'recentTrades' });
		}
	}, [isLoadingCachedMarketTrades, addLoadingItemToQueue]);

	useEffect(() => {
		if (cachedMarketTrades && cachedMarketTrades.records.length > 0) {
			const trades = sortUIOrderActionRecords(
				cachedMarketTrades.records
			) as UISerializableOrderActionRecord[];
			// TODO: do we need to get the most recent trade score from the server? it's not used in logic or rendered in a component
			handleNewTradesResult({ trades, mostRecentTradeScore: 0 });
		}
	}, [cachedMarketTrades, handleNewTradesResult]);
};

export default useMarketHistory;
